import { useMemo, useState } from 'react';

export type SortKey =
  | 'name-asc'
  | 'name-desc'
  | 'created-desc'
  | 'created-asc'
  | 'updated-desc'
  | 'updated-asc';

export type ListControlsOptions<T> = {
  getSearchHaystack: (item: T) => string;
  getName: (item: T) => string;
  getCreatedAt?: (item: T) => string | undefined;
  getUpdatedAt?: (item: T) => string | undefined;
  defaultSort?: SortKey;
};

export type ListControlsResult<T> = {
  search: string;
  setSearch: (value: string) => void;
  sort: SortKey;
  setSort: (value: SortKey) => void;
  visible: T[];
};

function compareString(a: string | undefined, b: string | undefined): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a < b ? -1 : 1;
}

export function useListControls<T>(
  items: T[],
  opts: ListControlsOptions<T>,
): ListControlsResult<T> {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>(opts.defaultSort ?? 'updated-desc');

  // Pre-compute haystacks once per items reference change
  const haystacks = useMemo(
    () => items.map((item) => opts.getSearchHaystack(item).toLowerCase()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? items.filter((_, i) => haystacks[i].includes(needle))
      : items.slice();

    const cmp = (a: T, b: T): number => {
      switch (sort) {
        case 'name-asc':
          return opts.getName(a).localeCompare(opts.getName(b));
        case 'name-desc':
          return opts.getName(b).localeCompare(opts.getName(a));
        case 'created-asc':
          return compareString(opts.getCreatedAt?.(a), opts.getCreatedAt?.(b));
        case 'created-desc':
          return compareString(opts.getCreatedAt?.(b), opts.getCreatedAt?.(a));
        case 'updated-asc':
          return compareString(opts.getUpdatedAt?.(a), opts.getUpdatedAt?.(b));
        case 'updated-desc':
        default:
          return compareString(opts.getUpdatedAt?.(b), opts.getUpdatedAt?.(a));
      }
    };

    filtered.sort(cmp);
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, haystacks, search, sort]);

  return { search, setSearch, sort, setSort, visible };
}
